/**
 * Banner de Câmeras Offline
 * Exibe um alerta no topo de qualquer página quando há câmeras offline ou em alerta.
 * Visível para TODOS os usuários (não apenas admins).
 * Atualiza a cada 5 minutos automaticamente.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { WifiOff, AlertTriangle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { cameraService, Camera } from '../services/cameraService';
import {
  getCameraConnectivityStatus,
  formatLastSeen,
} from './cameras/CameraStatusBadge';

// Número máximo de câmeras exibidas na linha colapsada
const MAX_INLINE = 1;

export function OfflineCamerasBanner() {
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchCameras = useCallback(async () => {
    try {
      const all = await cameraService.listCameras();
      const problematic = all.filter((c: Camera) => {
        // ⛔ Câmeras desabilitadas NÃO contam como offline
        if (c.enabled === false) return false;
        const connectivity = getCameraConnectivityStatus(c.last_seen);
        return connectivity === 'offline' || connectivity === 'warning';
      });
      setCameras(problematic);
      // Se chegou câmera nova com problema, re-exibe o banner
      if (problematic.length > 0) setDismissed(false);
    } catch {
      // silencioso — não quebrar o layout por falha de rede
    }
  }, []);

  useEffect(() => {
    fetchCameras();
    const interval = setInterval(fetchCameras, 5 * 60 * 1000); // 5 min
    return () => clearInterval(interval);
  }, [fetchCameras]);

  if (cameras.length === 0 || dismissed) return null;

  const offline = cameras.filter(
    (c) => getCameraConnectivityStatus(c.last_seen) === 'offline'
  );
  const warning = cameras.filter(
    (c) => getCameraConnectivityStatus(c.last_seen) === 'warning'
  );

  const hasOffline = offline.length > 0;
  const hasMultiple = cameras.length > 1;

  const bannerColor = hasOffline
    ? 'bg-red-50 border-red-300 dark:bg-red-950 dark:border-red-700'
    : 'bg-yellow-50 border-yellow-300 dark:bg-yellow-950 dark:border-yellow-700';
  const iconColor = hasOffline ? 'text-red-500' : 'text-yellow-500';
  const titleColor = hasOffline
    ? 'text-red-800 dark:text-red-200'
    : 'text-yellow-800 dark:text-yellow-200';
  const textColor = hasOffline
    ? 'text-red-700 dark:text-red-300'
    : 'text-yellow-700 dark:text-yellow-300';
  const rowHoverColor = hasOffline
    ? 'hover:bg-red-100 dark:hover:bg-red-900'
    : 'hover:bg-yellow-100 dark:hover:bg-yellow-900';
  const borderColor = hasOffline
    ? 'border-red-200 dark:border-red-800'
    : 'border-yellow-200 dark:border-yellow-800';

  // Conta total no título: "2 offline, 1 em alerta"
  const totalsLabel = [
    offline.length > 0 ? `${offline.length} offline` : '',
    warning.length > 0 ? `${warning.length} em alerta` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  // Câmeras exibidas inline (colapsado)
  const inlineCameras = cameras.slice(0, MAX_INLINE);
  const hiddenCount = cameras.length - MAX_INLINE;

  return (
    <div
      className={`border-b ${bannerColor} px-3 py-2 transition-all duration-300`}
      role="alert"
    >
      {/* ── Linha principal ── */}
      <div className="flex items-start gap-2 min-w-0">
        {/* Ícone */}
        <div className="flex-shrink-0 mt-0.5">
          {hasOffline ? (
            <WifiOff className={`w-4 h-4 ${iconColor}`} />
          ) : (
            <AlertTriangle className={`w-4 h-4 ${iconColor}`} />
          )}
        </div>

        {/* Conteúdo central: título + câmeras inline */}
        <div className="flex-1 min-w-0">
          {/* Título */}
          <span className={`font-semibold text-xs sm:text-sm leading-tight ${titleColor}`}>
            {hasOffline ? '🔴' : '🟡'}{' '}
            {cameras.length === 1
              ? 'Câmera com problema:'
              : `${cameras.length} câmeras com problema (${totalsLabel}):`}
          </span>

          {/* Câmeras inline (colapsado) — quebra linha em mobile */}
          {!expanded && (
            <span className={`block text-xs sm:text-sm leading-snug mt-0.5 sm:mt-0 sm:inline sm:ml-1 ${textColor}`}>
              {inlineCameras.map((c) => (
                <span key={c.id} className="inline">
                  <span className="font-medium break-words">{c.name}</span>
                  {c.location && (
                    <span className="opacity-60 hidden sm:inline"> · {c.location}</span>
                  )}
                  <span className="ml-1 opacity-80">
                    ({formatLastSeen(c.last_seen)})
                  </span>
                </span>
              ))}
              {hiddenCount > 0 && (
                <span className="opacity-60 ml-1">
                  +{hiddenCount} câmera{hiddenCount > 1 ? 's' : ''}
                </span>
              )}
            </span>
          )}
        </div>

        {/* Botões — sempre no canto direito, sem encolher */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {hasMultiple && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium hover:bg-black/10 transition-colors ${textColor}`}
              title={expanded ? 'Recolher' : 'Ver todas'}
            >
              {expanded ? (
                <><span className="hidden sm:inline">Recolher</span> <ChevronUp className="w-3 h-3" /></>
              ) : (
                <><span className="hidden sm:inline">Ver todas</span> <ChevronDown className="w-3 h-3" /></>
              )}
            </button>
          )}
          <button
            onClick={() => setDismissed(true)}
            className={`p-1 rounded hover:bg-black/10 transition-colors ${textColor}`}
            title="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Lista expandida ── */}
      {expanded && (
        <ul className={`mt-2 ml-6 space-y-0 border-t ${borderColor} pt-2`}>
          {cameras.map((c) => {
            const connectivity = getCameraConnectivityStatus(c.last_seen);
            const isOffline = connectivity === 'offline';
            return (
              <li
                key={c.id}
                className={`flex items-center gap-2 py-1.5 px-2 rounded text-xs ${textColor} ${rowHoverColor} transition-colors min-w-0`}
              >
                {/* Status badge */}
                <span
                  className={`flex-shrink-0 font-bold ${
                    isOffline
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-yellow-600 dark:text-yellow-400'
                  }`}
                >
                  {isOffline ? '🔴' : '🟡'}
                </span>

                {/* Nome + Local */}
                <span className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-1.5">
                  <span className="font-semibold truncate">{c.name}</span>
                  {c.location && (
                    <span className="opacity-60 truncate hidden sm:inline">📍 {c.location}</span>
                  )}
                </span>

                {/* IP — oculto em telas muito pequenas */}
                {c.ip_address && (
                  <span className="opacity-50 font-mono flex-shrink-0 hidden sm:inline">{c.ip_address}</span>
                )}

                {/* Tempo desde última atividade */}
                <span className="flex-shrink-0 opacity-80">
                  🕐 {formatLastSeen(c.last_seen)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
